import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import { useBusinessOperations } from "./useBusinessOperations";
import { useUser } from "src/shared/context/UserContext";
import { getShiftTypesOptions, normalizeShiftTypes } from "src/shared/utils/normalizeShiftTypes";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";
import { useReactToPrint } from "react-to-print";
import DailyReportPrinter from "src/print-configuration/DailyReportPrinter";
import { BusinessDay, BusinessSummary } from "./types";
import { logout } from "src/shared/components/LogOut";

interface OpenCloseDayPopupProps {
  mode: "open" | "close";
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (businessId?: string) => void;
  onPrintComplete?: () => void;
  businessId?: string;
  shiftTypes?: string[];
}

const extractId = (id: any): string | null => {
  if (!id) return null;
  if (typeof id === "string") return id;
  if (id.$oid) return id.$oid;
  if (id._id) return extractId(id._id);
  return null;
};

const OpenCloseDayPopup: React.FC<OpenCloseDayPopupProps> = ({ mode, isOpen, onClose, onSuccess, onPrintComplete, businessId, shiftTypes }) => {
  const navigate = useNavigate();
  const { openDay, closeDay, getBusinessSummary, loading } = useBusinessOperations();
  const { userConfigration } = useUser();

  const operationType = userConfigration?.businessOperationType || "Day";
  const isShiftMode = operationType === "Shift";
  // Get shiftTypes from user configuration or fallback
  const availableShifts = normalizeShiftTypes(shiftTypes?.length ? shiftTypes : userConfigration?.shiftTypes);
  const shiftOptions = getShiftTypesOptions(availableShifts);

  const [startingCash, setStartingCash] = useState<string>("");
  const [businessDate, setBusinessDate] = useState<Date>(new Date());
  const [notes, setNotes] = useState<string>("");
  const [shiftType, setShiftType] = useState<string>(availableShifts[0] || "Morning Shift");
  const [summaryData, setSummaryData] = useState<BusinessDay | null>(null);
  const [counted, setCounted] = useState<string>("");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState<boolean>(false);
  const [, setShouldPrint] = useState<boolean>(false);

  const hasFetchedRef = useRef(false);
  const isMountedRef = useRef(true);

  const FormatAmount = useFormatAmount();

  const printRef = useRef<HTMLDivElement>(null);

  // Handle close day with proper state management
  const handleCloseDay = async () => {
    if (!businessId) {
      console.error("No business ID provided for closing");
      return;
    }

    try {
      setIsClosing(true);
      const extractedId = extractId(businessId);

      if (!extractedId) {
        console.error("Could not extract valid ID from:", businessId);
        return;
      }

      await closeDay(extractedId, { closingCash: Number(counted || 0) });

      // Use setTimeout to ensure the print dialog doesn't block
      setTimeout(() => {
        handlePrint();
      }, 100);
    } catch (error) {
      console.error("Failed to close day:", error);
      setIsClosing(false);
    }
  };

  // Update the print handler to close popup after print starts
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "sales-report",
    pageStyle: `
    @page {
      size: 80mm 297mm;
      margin: 0;
    }
    @media print {
      html, body {
        margin: 0;
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      .print-content {
        width: 72mm;
        margin: 0 auto;
        background: white;
        padding: 2mm;
        font-family: 'Courier New', Courier, monospace;
        color: black;
      }
    }
  `,
    onBeforeGetContent: () => {},
    onPrintError: (errorLocation, error) => {
      console.error("Print error:", errorLocation, error);
      // Close popup even if print fails
      onClose();
      setShowConfirmPopup(false);
      setIsClosing(false);
    },
    onAfterPrint: () => {
      setShouldPrint(false);

      // Close the popup after print completes
      onClose();
      setShowConfirmPopup(false);
      setIsClosing(false);

      // Call the onPrintComplete callback when printing is done
      if (onPrintComplete) {
        onPrintComplete();
      } else {
      }
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      hasFetchedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Don't fetch if we're in the process of closing
    if (isClosing) {
      return;
    }

    // Reset fetch flag when popup closes or businessId changes
    if (!isOpen || !businessId) {
      hasFetchedRef.current = false;
      return;
    }

    const fetchSummary = async () => {
      // Prevent multiple fetches
      if (hasFetchedRef.current) {
        return;
      }

      const extractedId = extractId(businessId);

      if (!extractedId) {
        console.error("Could not extract valid ID from:", businessId);
        if (isMountedRef.current) {
          setFetchError("Invalid business ID format");
        }
        return;
      }

      try {
        if (isMountedRef.current) {
          setIsFetching(true);
          setFetchError(null);
        }
        hasFetchedRef.current = true;

        const response = await getBusinessSummary(extractedId);

        // Only update state if component is still mounted
        if (isMountedRef.current) {
          const data = response?.result || response;

          if (data) {
            setSummaryData(data);

            // const openingCash = data?.openingCash || 0;
            // const totalCashSales = data?.summary?.totalCashSales || 0;
            // const expected = openingCash + totalCashSales;

            setCounted("");
          } else {
            setFetchError("No data received from server");
          }
        }
      } catch (error: any) {
        console.error("Failed to fetch summary:", error);
        if (isMountedRef.current) {
          setFetchError(error?.message || "Failed to fetch summary");
          hasFetchedRef.current = false;
        }
      } finally {
        if (isMountedRef.current) {
          setIsFetching(false);
        }
      }
    };

    if (mode === "close" && isOpen && businessId) {
      fetchSummary();
    }
  }, [mode, isOpen, businessId, getBusinessSummary, isClosing]);

  const handleConfirmClose = () => {
    setShowConfirmPopup(true);
  };

  const handleCancelClose = () => {
    setShowConfirmPopup(false);
  };

  // Don't render if not open
  if (!isOpen) return null;

  const openingBalance = summaryData?.openingCash ?? 0;
  const summary: BusinessSummary = summaryData?.summary ?? ({} as BusinessSummary);

  const totalSales = summary.totalSales ?? 0;
  const numberOfSales = summary.numberOfSales ?? 0;
  const numberOfPendingOrders = summary.numberOfPendingOrders ?? 0;
  const receivableAmount = summary.receivableAmount ?? 0;
  const totalCashSales = summary.totalCashSales ?? 0;
  const totalCashTip = summary.totalCashTip;
  const totalCardTip = summary.totalCardTip;
  const totalCardSales = summary.totalCardSales ?? 0;
  const totalTips = Number(summary.totalTips);
  const totalDiscount = summary.discount ?? 0;
  const type = summaryData?.type ?? "N/A";

  const expected = openingBalance + totalCashSales;
  const difference = Number(counted) - expected;
  const businessDay = summaryData?.businessDay ?? "";
  const shift = summaryData?.shift ?? "no shift";
  const topSales = summaryData?.topSales ?? [];

  const handleOpenDaySubmit = async () => {
    try {
      const response = await openDay({
        businessDate,
        openingCash: Number(startingCash || 0),
        notes,
        shiftType: isShiftMode ? shiftType : undefined,
        operationType: operationType,
      });

      onClose();
      onSuccess?.(response?._id);
    } catch (error) {
      console.error("Failed to open day:", error);
    }
  };

  const totalAmount = summary.totalAmount;
  const isStartingCashValid = Number(startingCash) > 0;

  const isShiftValid = isShiftMode ? !!shiftType : true;

  const isOpenFormValid = isStartingCashValid && isShiftValid;

  const openButtonLabel = isShiftMode ? "Open Shift" : "Open Day";
  const closeButtonLabel = isShiftMode ? "Close Shift" : "Close Day";
  const openLabel = isShiftMode ? "Start Shift" : "Open Business Day";
  const closeLabel = isShiftMode ? "End Shift" : "Close Business Day";
  const closeWarningTitle = isShiftMode ? "Are you sure you want to close this shift?" : "Are you sure you want to close the day?";
  const closeWarningDescription = isShiftMode ? "This will finalize all shift sales and reports!" : "This will finalize all daily sales and reports!";

  return (
    <>
      <ModalWrapper title={mode === "open" ? openLabel : closeLabel} onClose={onClose} width="max-w-md" showCloseButton={mode === "close" && !isClosing}>
        <div className="w-full">
          {mode === "open" && (
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-600 font-medium mb-2">Opening Cash Drawer</label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-700 mr-2">Starting Cash :</span>
                  <input
                    type="number"
                    value={startingCash}
                    onChange={(e) => setStartingCash(e.target.value)}
                    className="flex-1 border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className={isShiftMode ? "flex space-x-4" : "block"}>
                {isShiftMode && (
                  <div className="flex-1">
                    <label className="text-sm text-gray-600 font-medium">Shift Type :</label>
                    <select
                      value={shiftType}
                      onChange={(e) => setShiftType(e.target.value)}
                      className="w-full mt-1 border rounded-md px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {shiftOptions.map((shift) => (
                        <option key={shift.value} value={shift.value}>
                          {shift.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="flex items-center">
                  <span className="text-sm text-gray-700 mr-1">Business Date :</span>

                  <DatePicker
                    selected={businessDate}
                    onChange={(date: Date | null) => date && setBusinessDate(date)}
                    dateFormat="dd-MM-yyyy"
                    wrapperClassName="flex-1"
                    className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 font-medium">Notes :</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full mt-1 border rounded-md px-3 py-2 h-20 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Add any notes about this shift..."
                />
              </div>

              <button
                onClick={handleOpenDaySubmit}
                disabled={loading || !isOpenFormValid}
                className={`w-full py-3 rounded-lg transition-colors font-medium
                          ${isOpenFormValid ? "bg-green-600 hover:bg-green-700 text-white" : "bg-gray-300 text-gray-500 cursor-not-allowed"}
                          `}
              >
                {loading ? "Processing..." : openButtonLabel}
              </button>
            </div>
          )}

          {mode === "close" && (
            <div className="text-black -m-2 space-y-2">
              {/* Loading State */}
              {isFetching && (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600 mb-4"></div>
                  <p className="text-gray-600">Loading data...</p>
                </div>
              )}

              {/* Error State */}
              {!isFetching && fetchError && (
                <div className="text-center py-8 text-red-600">
                  <p>Error loading data</p>
                </div>
              )}

              {/* No Data State */}
              {!isFetching && !fetchError && !summaryData && (
                <div className="text-center py-8">
                  <p>No data available</p>
                </div>
              )}

              {/* Data Display */}
              {!isFetching && !fetchError && summaryData && (
                <>
                  {/* Shift Info - Display if available */}
                  {summaryData.type === "shift" && summaryData.shift && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                      <p className="text-sm font-medium text-blue-800">Shift: {summaryData.shift}</p>
                    </div>
                  )}

                  <div className="rounded-lg border p-2">
                    <h3 className="font-semibold -mb-1">Sales Summary</h3>
                    <div className="flex justify-between text-sm">
                      <span>Total Sales:</span>
                      <span>{FormatAmount(totalSales)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Total Orders:</span>
                      <span>{numberOfSales}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Pending Orders:</span>
                      <span>{numberOfPendingOrders}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Receivable Amount:</span>
                      <span>{FormatAmount(receivableAmount)}</span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-2">
                    <h3 className="font-semibold -mb-1">Cash Count</h3>

                    <div className="flex justify-between text-sm">
                      <span>Opening Balance:</span>
                      <span>{FormatAmount(openingBalance)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Total Cash Sales:</span>
                      <span>{FormatAmount(totalCashSales)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Total Cash Tips:</span>
                      <span>{FormatAmount(totalCashTip)}</span>
                    </div>

                    <div className="flex justify-between ml-0.5 text-sm">
                      <span>Expected:</span>
                      <span>{FormatAmount(expected)}</span>
                    </div>

                    <div className="flex justify-between ml-0.5 items-center text-sm">
                      <span>Counted:</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={counted}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          setCounted(value);
                        }}
                        onBlur={() => {
                          if (counted !== "") {
                            setCounted(Number(counted).toFixed(2));
                          }
                        }}
                        className="w-28 border rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        disabled={isClosing}
                        placeholder="0.00"
                      />
                    </div>

                    <div className={`flex justify-between ml-0.5 mt-2 text-sm font-medium ${difference < 0 ? "text-red-600" : "text-green-600"}`}>
                      <span>Difference:</span>
                      <span>
                        {difference > 0 && "+"}
                        {FormatAmount(difference)}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-lg border p-2">
                    <h3 className="font-semibold -mb-1">Payment Breakdown</h3>

                    <div className="flex justify-between text-sm">
                      <span>Total Cash Amount:</span>
                      <span>{FormatAmount(totalCashSales)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Total Card Amount:</span>
                      <span>{FormatAmount(totalCardSales)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>
                        Total Tip ({totalCashTip} + {totalCardTip}):
                      </span>
                      <span>{FormatAmount(totalTips)}</span>
                    </div>

                    <div className="flex justify-between text-sm">
                      <span>Total Discount:</span>
                      <span>- {FormatAmount(totalDiscount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold border-t mt-2 pt-2">
                      <span>Total Amount:</span>
                      <span>{FormatAmount(totalAmount)}</span>
                    </div>
                  </div>

                  <div className="flex gap-4 mt-4">
                    {/* Close Day */}
                    <button
                      onClick={handleConfirmClose}
                      disabled={loading || isClosing || !businessId || isFetching}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg disabled:opacity-50 transition-colors font-medium"
                    >
                      {isClosing || loading ? "Processing..." : closeButtonLabel}
                    </button>

                    {/* Skip & Logout */}
                    <button
                      onClick={async () => {
                        try {
                          // Call the actual logout function
                          await logout(navigate);
                          // Close the popup
                          onClose();
                          // Optionally call success callback
                          onSuccess?.();
                        } catch (err) {
                          console.error("Logout failed:", err);
                        }
                      }}
                      disabled={isClosing || isFetching}
                      className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg disabled:opacity-50 transition-colors font-medium"
                    >
                      SKIP & LOGOUT
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </ModalWrapper>

      {/* Confirmation Popup */}
      {showConfirmPopup && (
        <ModalWrapper title={isShiftMode ? "Confirm the business shift's close." : "Confirm the business day's close."} onClose={handleCancelClose} width="max-w-lg">
          <div className="p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl text-red-600">⚠</span>
              <div>
                <p className="font-semibold text-red-600 text-lg">{closeWarningTitle}</p>
                <p className="text-sm text-gray-600 mt-1">{closeWarningDescription}</p>

                {/* Pending Orders Warning */}
                {numberOfPendingOrders > 0 && (
                  <div className="mt-3 p-2 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <p className="text-sm font-medium text-yellow-800">
                      ⚠ You have <span className="font-bold">{numberOfPendingOrders}</span> pending order{numberOfPendingOrders !== 1 ? "s" : ""}!
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">These orders will be marked as incomplete if you close now.</p>
                  </div>
                )}

                {/* Print notification */}
                <div className="mt-3 p-2 bg-blue-50 border border-blue-300 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">🖨️ A report will be automatically printed after confirmation.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={handleCancelClose} disabled={isClosing} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 rounded-lg font-medium disabled:opacity-50">
                Cancel
              </button>
              <button onClick={handleCloseDay} disabled={isClosing} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg font-medium disabled:opacity-50">
                {isClosing ? "Processing..." : "Confirm & Print"}
              </button>
            </div>
          </div>
        </ModalWrapper>
      )}

      <div
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: "80mm",
          backgroundColor: "white",
          zIndex: -1,
          opacity: 0,
          pointerEvents: "none",
        }}
      >
        <div ref={printRef} className="print-content">
          <DailyReportPrinter
            businessDay={businessDay}
            type={type}
            shift={shift}
            totalSales={totalSales}
            totalOrders={numberOfSales}
            openingBalance={openingBalance}
            totalCashSales={totalCashSales}
            totalCardSales={totalCardSales}
            totalTips={totalTips}
            totalDiscounts={totalDiscount}
            expected={expected}
            counted={counted}
            difference={difference}
            topSales={topSales}
            totalAmount={totalAmount}
            numberOfPendingOrders={numberOfPendingOrders}
            receivableAmount={receivableAmount}
            totalCashTips={totalCashTip}
          />
        </div>
      </div>
    </>
  );
};

export default OpenCloseDayPopup;
